import React from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendIcon from "@mui/icons-material/Send";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface RfsWizardNavigationProps {
  activeStep: number;
  totalSteps: number;
  isSubmitting: boolean;
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const RfsWizardNavigation = ({
  activeStep,
  totalSteps,
  isSubmitting,
  onNext,
  onBack,
  onSaveDraft,
  onSubmit,
  onCancel,
}: RfsWizardNavigationProps) => {
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const isMobile = useMediaQuery("(max-width:768px)");
  const isLastStep = activeStep === totalSteps - 1;
  const isFirstStep = activeStep === 0;

  /* ── Shared button base ── */
  const btnBase = {
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "none" as const,
    borderRadius: dt.radiusPill,
    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
  };

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        background: dt.cardBg,
        borderTop: `1px solid ${dt.cardBorder}`,
        boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
        px: isMobile ? 2 : 4,
        py: 2,
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: "1200px",
          mx: "auto",
          gap: 2,
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Left side -- Back / Cancel */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            order: isMobile ? 2 : 1,
            flex: isMobile ? "1 1 100%" : "auto",
          }}
        >
          {!isFirstStep && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
              onClick={onBack}
              sx={{
                ...btnBase,
                borderColor: dt.cardBorder,
                color: dt.textSecondary,
                "&:hover": {
                  borderColor: "#D1D5DB",
                  background: "#F9FAFB",
                  transform: "translateX(-2px)",
                },
                flex: isMobile ? 1 : "auto",
              }}
            >
              {translate("common:buttons.back")}
            </Button>
          )}
          <Button
            variant="text"
            onClick={onCancel}
            sx={{
              ...btnBase,
              fontWeight: 500,
              color: dt.textMuted,
              "&:hover": {
                color: dt.textSecondary,
                background: "rgba(0,0,0,0.02)",
              },
              flex: isMobile ? 1 : "auto",
            }}
          >
            {translate("common:buttons.cancel")}
          </Button>
        </Box>

        {/* Right side -- Save Draft / Next / Publish */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            order: isMobile ? 1 : 2,
            flex: isMobile ? "1 1 100%" : "auto",
          }}
        >
          {activeStep > 0 && (
            <Button
              variant="outlined"
              startIcon={
                <SaveOutlinedIcon sx={{ fontSize: "16px !important" }} />
              }
              onClick={onSaveDraft}
              disabled={isSubmitting}
              sx={{
                ...btnBase,
                borderColor: dt.blue,
                color: dt.blue,
                "&:hover": {
                  borderColor: dt.blueHover,
                  background: "rgba(52, 152, 219, 0.04)",
                  transform: "translateY(-1px)",
                },
                flex: isMobile ? 1 : "auto",
              }}
            >
              {translate("myMachinery:saveAsDraft")}
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
              onClick={onNext}
              sx={{
                ...btnBase,
                background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
                boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                "&:hover": {
                  background: `linear-gradient(135deg, #2E86C1 0%, ${dt.blue} 100%)`,
                  boxShadow: "0 4px 14px rgba(52,152,219,0.3)",
                  transform: "translateY(-1px)",
                },
                flex: isMobile ? 1 : "auto",
                minWidth: "130px",
              }}
            >
              {translate("servicing:nextStep")}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={
                <SendIcon sx={{ fontSize: "16px !important" }} />
              }
              onClick={onSubmit}
              disabled={isSubmitting}
              sx={{
                ...btnBase,
                background:
                  "linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)",
                boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)",
                  boxShadow: "0 6px 20px rgba(39,174,96,0.3)",
                  transform: "translateY(-2px)",
                },
                "&.Mui-disabled": {
                  background: "#D1D5DB",
                  color: "#9CA3AF",
                  boxShadow: "none",
                },
                flex: isMobile ? 1 : "auto",
                minWidth: "150px",
              }}
            >
              {translate("myMachinery:publish")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RfsWizardNavigation;
